import React from 'react';
import PropTypes from 'prop-types';
import { Mutation, Query } from '@apollo/client/react/components';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  TextField,
  Typography,
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { Typeahead } from 'typeahead-component';
import { Button, Text, toastError, toastSuccess, ConfirmationModal } from 'bench-internal-theme';
import { GET_FINANCIAL_INSTITUTION, GET_FINANCIAL_INSTITUTIONS } from './queries';
import styles from './FunctionalAccountDetails.styles';
import { CREATE_FINANCIAL_INSTITUTION } from './mutations';
import { openModal } from '../modal-core-components';

const productMatchesSelectedLedgerCategory = (product, ledgerCategoryId) =>
  !ledgerCategoryId ||
  product.financialInstitutionProductType.ledgerCategory.id === ledgerCategoryId;

const productToSuggestions = product => ({
  label: product.financialInstitutionProductType.name,
  value: product.financialInstitutionProductType.id,
  inactive: !product.isActive,
  isDefault: product.financialInstitutionProductType.isDefault,
});

const findSelectedInstitution = (institutions, institutionId) =>
  institutions.find(institution => institution.id === institutionId);

export const INSTITUTIONS_LIMIT = 25;

const FunctionalAccountDetails = ({
  onChange,
  formValues,
  onUpdateProduct,
  ledgerCategoryId,
  classes,
  clientId,
  errors,
  showErrors,
}) => {
  const [query, setQuery] = React.useState(undefined);
  const [inactiveInstitutionModalOpen, setInactiveInstitutionModalOpen] = React.useState(false);

  const onInstitutionChange = onChange('institution');
  const onProductTypeChange = onChange('productType');
  const onLast4Change = onChange('accountLastFourDigits');

  const getProductsAsSuggestions = institution => {
    if (!institution?.products.length) return [];
    return institution.products
      .filter(product => productMatchesSelectedLedgerCategory(product, ledgerCategoryId))
      .map(productToSuggestions);
  };

  const formatInstitutionsToSuggestions = institutions => {
    if (!institutions?.length) return [];
    return institutions.map(institution => ({
      value: institution.id,
      label: institution.name,
      inactive: !institution.isActive,
    }));
  };

  const handleInstitutionInputChange = (value, { action }) => {
    // When an institution is selected from the dropdown the value is sent back as an empty string.
    // This triggers a refetch with the initial data which causes issues if the queried institution
    // isn't part of that data. So, we filter out those actions.
    if (action === 'input-change') {
      setQuery(value);
    }
  };

  const showInactiveInstitutionModalOpen = value => setInactiveInstitutionModalOpen(value);

  // eslint-disable-next-line consistent-return
  const handleInstitutionChange = (institutions, institution) => {
    if (!institution?.value) {
      onInstitutionChange(undefined);
      return setQuery(undefined);
    }

    if (institution.inactive) {
      showInactiveInstitutionModalOpen(true);
    }
    onInstitutionChange(institution);
    onProductTypeChange('');
    const { name } = findSelectedInstitution(institutions, institution.value);
    setQuery(name);
    // eslint-disable-next-line react/prop-types
    if (errors.productType) {
      showErrors({});
    }
  };

  const handleProductChange = (institution, productTypeId) => {
    if (!institution || !productTypeId) return;
    const selectedProduct = institution.products.find(
      product => product.financialInstitutionProductType.id === productTypeId,
    );
    onUpdateProduct(selectedProduct.id);
  };

  const handleProductTypeChange = (institution, productType) => {
    onProductTypeChange(productType);
    handleProductChange(institution, productType.value);
  };

  const handleProductTypeClick = () => {
    if (!formValues.institution && !ledgerCategoryId) {
      showErrors({ productType: { error: 'Institution and Category not selected' } });
    } else if (!formValues.institution) {
      showErrors({ productType: { error: 'Institution not selected' } });
    } else if (!ledgerCategoryId) {
      showErrors({ productType: { error: 'Category not selected' } });
    }
  };

  const handleLast4Change = event => {
    const digitsRegex = /^[0-9\b]+$/;
    const { value } = event.target;
    if (value === '' || digitsRegex.test(value)) {
      onLast4Change(value);
    }
  };

  const handleCreateFinancialInstitution = (createInstitutionMutation, name) =>
    createInstitutionMutation({
      variables: {
        institution: {
          name,
          isActive: true,
        },
        populateDefaults: true,
        clientId,
      },
    });

  const handleCreateFinancialInstitutionConfirmation = (createInstitutionMutation, name) => {
    openModal(closeModal => (
      <ConfirmationModal
        closeModal={closeModal}
        onConfirm={() => handleCreateFinancialInstitution(createInstitutionMutation, name)}
        title="Are you sure?"
        description="New institutions should only be created if you have a PDF or bank connection that states the institutions full name."
      />
    ));
  };

  const handleCreateFinancialInstitutionError = error => {
    toastError('Create Institution failed', error.message);
  };

  const handleCreateFinancialInstitutionSuccess = (
    { createFinancialInstitution },
    institutions,
  ) => {
    toastSuccess('Create Institution', `Successfully created ${createFinancialInstitution.name}`);
    const mappedInstitution = {
      value: createFinancialInstitution.id,
      label: createFinancialInstitution.name,
      inactive: !createFinancialInstitution.isActive,
    };
    handleInstitutionChange(institutions, mappedInstitution);
  };

  const institutionId = formValues?.institution?.value;

  return (
    <Grid container spacing={2} style={{ marginTop: '1.8rem' }}>
      <Grid item xs={12} style={{ marginBottom: '0.5rem' }}>
        <Typography>Functional account details</Typography>
      </Grid>
      <Query
        query={GET_FINANCIAL_INSTITUTIONS}
        variables={{ search: query, limit: INSTITUTIONS_LIMIT }}
      >
        {({ data, loading }) => {
          const institutionSuggestions = formatInstitutionsToSuggestions(
            data?.financialInstitutions,
          );
          return (
            <>
              <Mutation
                mutation={CREATE_FINANCIAL_INSTITUTION}
                awaitRefetchQueries
                refetchQueries={[
                  {
                    query: GET_FINANCIAL_INSTITUTIONS,
                    variables: { search: '', limit: INSTITUTIONS_LIMIT },
                  },
                ]}
                onCompleted={result =>
                  handleCreateFinancialInstitutionSuccess(result, data?.financialInstitutions)
                }
                onError={handleCreateFinancialInstitutionError}
              >
                {createFinancialInstitution => (
                  <Grid item xs={4}>
                    <FormControl fullWidth required error={errors.institution}>
                      <InputLabel
                        error={errors.institution}
                        classes={{ root: classes.label, shrink: classes.label }}
                        disableAnimation
                        shrink
                        className={classes.labelSpacer}
                      >
                        Institution
                      </InputLabel>
                      <Typeahead
                        isCreatable
                        isLoading={loading}
                        suggestions={institutionSuggestions}
                        onChange={value =>
                          handleInstitutionChange(data?.financialInstitutions, value)
                        }
                        onCreateNew={value =>
                          handleCreateFinancialInstitutionConfirmation(
                            createFinancialInstitution,
                            value,
                          )
                        }
                        onInputChange={handleInstitutionInputChange}
                        placeholder="Search institutions"
                        value={formValues.institution}
                        hasError={errors.institution}
                      />
                      <FormHelperText error={errors.institution}>
                        {errors.institution?.error}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
              </Mutation>
              <Grid item xs={4}>
                <Query
                  query={GET_FINANCIAL_INSTITUTION}
                  variables={{ id: institutionId }}
                  skip={!institutionId}
                >
                  {({ data: institutionData, loading: institutionLoading }) => {
                    const products = getProductsAsSuggestions(
                      institutionData?.financialInstitution,
                    );
                    return (
                      <FormControl
                        fullWidth
                        required
                        error={errors.productType}
                        onClick={handleProductTypeClick}
                        data-testid="form-control"
                      >
                        <div className={classes.temporaryLedgerLabelContainer}>
                          <InputLabel
                            error={errors.productType}
                            classes={{ root: classes.label, shrink: classes.label }}
                            disableAnimation
                            shrink
                            className={classes.labelSpacer}
                          >
                            Product Type
                          </InputLabel>
                        </div>
                        <Typeahead
                          isLoading={institutionId && institutionLoading}
                          suggestions={products}
                          placeholder="Search products"
                          disabled={!(formValues.institution && ledgerCategoryId)}
                          onChange={value =>
                            handleProductTypeChange(institutionData?.financialInstitution, value)
                          }
                          value={formValues.productType}
                          hasError={errors.productType}
                          noOptionsMessage={() => (
                            <>
                              <Text as="p" styleType="mediumBold">
                                Request new product type
                              </Text>
                              <p>
                                <em>
                                  Please request a new product type in the #institution_admins slack
                                  channel
                                </em>
                              </p>
                            </>
                          )}
                        />
                        <FormHelperText error={errors.productType}>
                          {errors.productType?.error}
                        </FormHelperText>
                      </FormControl>
                    );
                  }}
                </Query>
              </Grid>
              <Grid item xs={4}>
                <InputLabel
                  error={errors.institution}
                  classes={{ root: classes.label, shrink: classes.label }}
                  disableAnimation
                  shrink
                >
                  Account Number
                </InputLabel>
                <TextField
                  id="accountLastFourDigits"
                  label="Last 4 Digits"
                  defaultValue={formValues.accountLastFourDigits || null}
                  onChange={handleLast4Change}
                  variant="filled"
                  inputProps={{ className: classes.inputStyle, maxLength: 4 }}
                />
              </Grid>
            </>
          );
        }}
      </Query>
      <Dialog open={inactiveInstitutionModalOpen}>
        <DialogTitle>Inactive Institution</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You have selected an inactive institution. Do you wish to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => showInactiveInstitutionModalOpen(false)} autoFocus>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

FunctionalAccountDetails.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  formValues: PropTypes.object.isRequired,
  ledgerCategoryId: PropTypes.string,
  showErrors: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  onUpdateProduct: PropTypes.func.isRequired,
  errors: PropTypes.shape({
    error: PropTypes.string,
    institution: PropTypes.object,
    productType: PropTypes.object,
  }),
  // eslint-disable-next-line react/forbid-prop-types
  classes: PropTypes.object.isRequired,
  clientId: PropTypes.number.isRequired,
};

FunctionalAccountDetails.defaultProps = {
  ledgerCategoryId: undefined,
  errors: {},
};

export default withStyles(styles)(FunctionalAccountDetails);
